import { ColorField, ColorArea, ColorSlider, ColorSwatchPicker } from "./index";
import * as React from "react";
import { Color, parseColor } from "@react-stately/color";
import { Flex } from "../flex";
import { NumberInput } from "../input";
import { Space } from "../space";
import { Select } from "../select";
import { Separator } from "../separator";
import { useControlledState } from "../../hooks/use-controlled-state";
import { StyleProps } from "../../utils/types";

type ColorPickerProps = StyleProps<
  "wrapper" | "area" | "swatch" | "hue" | "alpha"
> & {
  onChange?: (color: Color) => void;
  color?: Color;
};

export function ColorPicker({
  onChange,
  color: colorProp,
  styles,
  classNames,
  className,
  style,
}: ColorPickerProps) {
  const [colorSpace, setColorSpace] = React.useState("hex");

  const [color, setColor] = useControlledState(
    colorProp,
    parseColor("#BB66CC").toFormat("hsla"),
    onChange
  );

  function handleChange(color: Color) {
    setColor(color);
    onChange?.(color);
  }

  function setAlpha(alpha: number) {
    if (isNaN(alpha)) {
      return;
    }

    const newColor = color.withChannelValue("alpha", alpha);
    setColor(newColor);
  }

  const [h, s, l] = color.getColorChannels();
  const rgbaColor = color.toFormat("rgba");
  const [r, g, b] = rgbaColor.getColorChannels();
  return (
    <Flex stack style={{ width: 240 }}>
      <ColorArea
        onChange={handleChange}
        onChangeEnd={handleChange}
        xChannel={s}
        yChannel={l}
        value={color ?? parseColor("hsl(219, 79%, 66%)")}
        style={{ width: "100%", height: 140 }}
      />
      <Flex stack style={{ padding: 8 }}>
        <Flex stack gap={"2xs"}>
          <ColorSlider
            value={color}
            onChange={handleChange}
            onChangeEnd={handleChange}
            channel={h}
          />
          <ColorSlider
            value={color}
            onChange={handleChange}
            onChangeEnd={handleChange}
            channel={"alpha"}
          />
        </Flex>
        <Flex stack>
          <Flex style={{ width: "100%" }} gap={"3xs"}>
            {colorSpace === "hex" && (
              <Space style={{ gap: 3 }} compact>
                <ColorField
                  value={color}
                  onChange={handleChange as any}
                  size="sm"
                  style={{ width: "60%" }}
                  appearance="filled"
                  placeholder="Add a color"
                ></ColorField>
                <NumberInput
                  onChange={(n) => setAlpha(n)}
                  maxValue={1}
                  formatOptions={{
                    style: "percent",
                    maximumFractionDigits: 0,
                  }}
                  size="sm"
                  value={color.getChannelValue("alpha")}
                  style={{ width: "40%" }}
                  appearance="filled"
                ></NumberInput>
              </Space>
            )}

            {["rgb", "hsl"].includes(colorSpace) && (
              <Space style={{ gap: 3 }} compact>
                <NumberInput
                  value={
                    colorSpace === "rgb"
                      ? rgbaColor.getChannelValue(r)
                      : color.getChannelValue(h)
                  }
                  appearance="filled"
                  size="sm"
                ></NumberInput>
                <NumberInput
                  value={
                    colorSpace === "rgb"
                      ? rgbaColor.getChannelValue(g)
                      : color.getChannelValue(s)
                  }
                  appearance="filled"
                  size="sm"
                ></NumberInput>
                <NumberInput
                  value={
                    colorSpace === "rgb"
                      ? rgbaColor.getChannelValue(b)
                      : color.getChannelValue(l)
                  }
                  appearance="filled"
                  size="sm"
                ></NumberInput>
                <NumberInput
                  formatOptions={{
                    style: "percent",
                    maximumFractionDigits: 0,
                  }}
                  value={
                    colorSpace === "rgb"
                      ? rgbaColor.getChannelValue("alpha")
                      : color.getChannelValue("alpha")
                  }
                  appearance="filled"
                  size="sm"
                ></NumberInput>
              </Space>
            )}

            <Select
              style={{ width: "60%" }}
              valueKey="id"
              onChange={(v) =>
                setColorSpace((v.target.value as string) ?? colorSpace)
              }
              showSelectionIndicator={false}
              value={colorSpace}
              size="sm"
              appearance="filled"
              items={["hex", "rgb", "hsl"].map((o) => ({
                label: o.toUpperCase(),
                id: o,
              }))}
            ></Select>
          </Flex>

          <Separator color="gray" />

          <Flex>
            <ColorSwatchPicker
              styles={{
                wrapper: { gap: 6 },
                swatch: {
                  borderRadius: 5,
                },
              }}
              onChange={handleChange}
              colors={[
                "#000000",
                "#333333",
                "#555555",
                "#777777",
                "#999999",
                "#bbbbbb",
                "#dddddd",
                "#ffffff",
                "#4455BB",
                "#1199EE",
                "#55CCFF",
                "#22CCDD",
                "#22AA99",
                "#66BB66",
                "#99CC66",
                "#CCDD33",
                "#7755CC",
                "#BB66CC",
                "#FF88AA",
                "#EE4444",
                "#FF7744",
                "#FFAA22",
                "#FFCC66",
                "#FFEE66",
              ].map((color) => ({
                label: color,
                color: parseColor(color).toString("hsl"),
                key: color,
              }))}
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
